import {
  CompanyEmail,
  CompanyPhone,
  EmailType,
  PhoneType,
} from 'store/company/company.types'
import { parseFreeformAddress } from 'utils/parse-freeform-address'
import { formatPhoneForSave } from 'utils/phone'

export const DISPATCH_CONTACT_NAME = 'Dispatch'

export const findDispatchEmail = (emails: CompanyEmail[]) =>
  emails.find(
    (email) =>
      email.emailType === 'NotificationEmail' &&
      (email.contactName ?? '').toLowerCase() ===
        DISPATCH_CONTACT_NAME.toLowerCase()
  ) ??
  emails.find((email) => email.emailType === 'NotificationEmail')

export const findDispatchPhone = (phones: CompanyPhone[], phoneType: PhoneType) =>
  phones.find(
    (phone) =>
      phone.phoneType === phoneType &&
      (phone.contactName ?? '').toLowerCase() ===
        DISPATCH_CONTACT_NAME.toLowerCase()
  ) ?? phones.find((phone) => phone.phoneType === phoneType)

export const formatCompanyAddress = (company?: {
  streetNumber?: string
  streetName?: string
  unitNumber?: string
  city?: string
  state?: string
  zipCode?: string
}) => {
  if (!company) return ''
  const line1 = [company.streetNumber, company.streetName]
    .filter(Boolean)
    .join(' ')
    .trim()
  const line2 = company.unitNumber?.trim()
  const cityLine = [company.city, company.state, company.zipCode]
    .filter(Boolean)
    .join(', ')
    .trim()

  return [line1, line2, cityLine].filter(Boolean).join(', ')
}

export const parseCarrierAddress = (address: string) =>
  parseFreeformAddress(address)

export type CarrierContactMutations = {
  addEmail: (args: {
    companyId: string
    body: {
      email: string
      emailType: EmailType
      userType: 'company'
      contactName: string
    }
  }) => { unwrap: () => Promise<unknown> }
  updateEmail: (args: {
    companyEmailId: string
    body: {
      email: string
      emailType: EmailType
      contactName: string
    }
  }) => { unwrap: () => Promise<unknown> }
  addPhone: (args: {
    companyId: string
    body: {
      phone: string
      phoneType: PhoneType
      userType: 'company'
      contactName: string
    }
  }) => { unwrap: () => Promise<unknown> }
  updatePhone: (args: {
    companyPhoneId: string
    body: {
      phone: string
      phoneType: PhoneType
      contactName: string
    }
  }) => { unwrap: () => Promise<unknown> }
}

export const upsertDispatchEmail = async (
  companyId: string,
  email: string,
  existingEmails: CompanyEmail[],
  mutations: CarrierContactMutations
) => {
  const trimmed = email.trim()
  if (!trimmed) return

  const existing = findDispatchEmail(existingEmails)
  if (existing) {
    await mutations
      .updateEmail({
        companyEmailId: existing.companyEmailId,
        body: {
          email: trimmed,
          emailType: 'NotificationEmail',
          contactName: DISPATCH_CONTACT_NAME,
        },
      })
      .unwrap()
    return
  }

  await mutations
    .addEmail({
      companyId,
      body: {
        email: trimmed,
        emailType: 'NotificationEmail',
        userType: 'company',
        contactName: DISPATCH_CONTACT_NAME,
      },
    })
    .unwrap()
}

export const upsertDispatchPhone = async (
  companyId: string,
  phone: string,
  phoneType: PhoneType,
  existingPhones: CompanyPhone[],
  mutations: CarrierContactMutations
) => {
  const trimmed = formatPhoneForSave(phone.trim())
  if (!trimmed) return

  const existing = findDispatchPhone(existingPhones, phoneType)
  if (existing) {
    await mutations
      .updatePhone({
        companyPhoneId: existing.companyPhoneId,
        body: {
          phone: trimmed,
          phoneType,
          contactName: DISPATCH_CONTACT_NAME,
        },
      })
      .unwrap()
    return
  }

  await mutations
    .addPhone({
      companyId,
      body: {
        phone: trimmed,
        phoneType,
        userType: 'company',
        contactName: DISPATCH_CONTACT_NAME,
      },
    })
    .unwrap()
}
